import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../../../components/shared/button/Button';
import { TSCard } from '../../../../../components/shared/card/Card';
import { ConfirmDialog } from '../../../../../components/shared/modal/ConfirmDialog';
import { EmployeeSchedule, employeeScheduleToPattern } from '../../../../../helpers/BuildWorkingPattern';
import { secondsSinceMidnightToTimeString, timeStringToSecondsSinceMidnight } from '../../../../../helpers/dateHelper';
import { dayIndexes } from '../../../../../helpers/dayIndexes';
import { handleMutationResult } from '../../../../../helpers/toastHelper';
import { useContract } from '../../../../../services/contract/useContract';
import { useGetMeQuery, useUpdateWorkingPatternMutation } from '../../../../../services/gql/graphql.generated';
import strings from '../../../team.translations';
import { DEFAULT_END, DEFAULT_SPLIT_DURATION_SECONDS, DEFAULT_START } from '../../splitAvailabilityDefaults';
import { TeamMemberScheduleEditFormRow } from './TeamMemberScheduleEditFormRow';

interface ScheduleEditFormProps {
  userId: string;
  userFirstName: string;
  contractTypeId?: string;
  initSchedule: EmployeeSchedule[];
}

export const ScheduleEditForm: FC<ScheduleEditFormProps> = ({
  userId,
  userFirstName,
  contractTypeId,
  initSchedule
}) => {
  const [schedule, setSchedule] = useState<EmployeeSchedule[]>(initSchedule);
  const [updatePatternRes, updatePattern] = useUpdateWorkingPatternMutation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const contract = useContract(contractTypeId);

  const [{ data: userData, error: userError }] = useGetMeQuery();
  const isSelf = userData?.myEmployee?.identityId === userId;

  useEffect(() => {
    setSchedule(initSchedule);
  }, [initSchedule]);

  const replaceScheduleDay = (scheduleDay: EmployeeSchedule, update: Partial<EmployeeSchedule>) => {
    setSchedule(current =>
      current.map(day =>
        day.dayNumber === scheduleDay.dayNumber && day.slotNumber === scheduleDay.slotNumber
          ? { ...day, ...update }
          : day
      )
    );
  };

  const getDaySchedule = (dayNumber?: number) =>
    schedule
      .filter(item => item.dayNumber === dayNumber)
      .sort((left, right) => (left.slotNumber ?? 0) - (right.slotNumber ?? 0));

  const getSplitPlacement = (dayNumber?: number) => {
    const daySchedule = getDaySchedule(dayNumber);
    const firstSlot = daySchedule.find(item => (item.slotNumber ?? 0) === 0);

    if (daySchedule.length >= 2 || !firstSlot?.available || !firstSlot.start || !firstSlot.end) {
      return null;
    }

    const earliestStart = contract?.earliestStartTime ?? 0;
    const latestEnd = contract?.latestEndTime ?? 24 * 60 * 60;
    const currentStart = timeStringToSecondsSinceMidnight(firstSlot.start);
    const currentEnd = timeStringToSecondsSinceMidnight(firstSlot.end);
    const canAddBefore = currentStart > earliestStart;
    const canAddAfter = currentEnd < latestEnd;

    if (!canAddBefore && !canAddAfter) {
      return null;
    }

    return canAddAfter ? 'after' : 'before';
  };

  const canAddSplitPeriod = (dayNumber?: number) => {
    return getSplitPlacement(dayNumber) !== null;
  };

  const buildSplitDefaults = (scheduleDay: EmployeeSchedule) => {
    const firstSlot = getDaySchedule(scheduleDay.dayNumber).find(item => (item.slotNumber ?? 0) === 0);
    const placement = getSplitPlacement(scheduleDay.dayNumber) ?? 'after';
    const earliestStart = contract?.earliestStartTime ?? 0;
    const latestEnd = contract?.latestEndTime ?? 24 * 60 * 60;
    const currentStart = firstSlot?.start ? timeStringToSecondsSinceMidnight(firstSlot.start) : earliestStart;
    const currentEnd = firstSlot?.end ? timeStringToSecondsSinceMidnight(firstSlot.end) : latestEnd;

    if (placement === 'before') {
      const endSeconds = currentStart;
      const startSeconds = Math.max(earliestStart, endSeconds - DEFAULT_SPLIT_DURATION_SECONDS);

      return {
        start: secondsSinceMidnightToTimeString(startSeconds),
        end: secondsSinceMidnightToTimeString(endSeconds),
        placement
      };
    }

    const startSeconds = currentEnd;
    const endSeconds = Math.min(latestEnd, startSeconds + DEFAULT_SPLIT_DURATION_SECONDS);

    return {
      start: secondsSinceMidnightToTimeString(startSeconds),
      end: secondsSinceMidnightToTimeString(Math.max(endSeconds, startSeconds)),
      placement: 'after' as const
    };
  };

  const onCheck = (scheduleDay: EmployeeSchedule) => () => {
    if ((scheduleDay.slotNumber ?? 0) !== 0) {
      return;
    }

    if (scheduleDay.available) {
      setSchedule(current =>
        current
          .filter(item => !(item.dayNumber === scheduleDay.dayNumber && (item.slotNumber ?? 0) === 1))
          .map(item =>
            item.dayNumber === scheduleDay.dayNumber && (item.slotNumber ?? 0) === 0
              ? { ...item, available: false }
              : item
          )
      );
      return;
    }

    replaceScheduleDay(scheduleDay, {
      available: true,
      start: scheduleDay.start || DEFAULT_START,
      end: scheduleDay.end || DEFAULT_END
    });
  };

  const onStartChange = (scheduleDay: EmployeeSchedule) => (newVal?: string) => {
    replaceScheduleDay(scheduleDay, { start: newVal });
  };

  const onEndChange = (scheduleDay: EmployeeSchedule) => (newVal?: string) => {
    replaceScheduleDay(scheduleDay, { end: newVal });
  };

  const onAdd = (scheduleDay: EmployeeSchedule) => () => {
    setSchedule(current => {
      const currentDay = current
        .filter(item => item.dayNumber === scheduleDay.dayNumber)
        .sort((left, right) => (left.slotNumber ?? 0) - (right.slotNumber ?? 0));

      if (currentDay.length >= 2 || !currentDay[0]?.available) {
        return current;
      }

      const splitDefaults = buildSplitDefaults(scheduleDay);

      if (splitDefaults.placement === 'before') {
        return current
          .flatMap(item => {
            if (item.dayNumber !== scheduleDay.dayNumber) {
              return item;
            }

            if ((item.slotNumber ?? 0) === 0) {
              return [{ ...item, slotNumber: 1 }];
            }

            return item;
          })
          .concat({
            dayNumber: scheduleDay.dayNumber,
            slotNumber: 0,
            id: '',
            day: scheduleDay.day,
            start: splitDefaults.start,
            end: splitDefaults.end,
            available: true
          });
      }

      return [
        ...current,
        {
          dayNumber: scheduleDay.dayNumber,
          slotNumber: 1,
          id: '',
          day: scheduleDay.day,
          start: splitDefaults.start,
          end: splitDefaults.end,
          available: true
        }
      ];
    });
  };

  const onDelete = (scheduleDay: EmployeeSchedule) => () => {
    setSchedule(current => {
      const remaining = current.filter(
        item => !(item.dayNumber === scheduleDay.dayNumber && item.slotNumber === scheduleDay.slotNumber)
      );
      const dayItems = remaining
        .filter(item => item.dayNumber === scheduleDay.dayNumber)
        .sort((left, right) => (left.slotNumber ?? 0) - (right.slotNumber ?? 0));

      // If only one slot remains for the day and it is not slot 0, renumber it to slot 0.
      if (dayItems.length === 1 && (dayItems[0].slotNumber ?? 0) !== 0) {
        return remaining.map(item => (item.dayNumber === scheduleDay.dayNumber ? { ...item, slotNumber: 0 } : item));
      }

      return remaining;
    });
  };

  const validationErrors = useMemo(() => {
    const errors: string[] = [];

    dayIndexes.forEach(dayNumber => {
      const daySchedule = schedule
        .filter(item => item.dayNumber === dayNumber)
        .sort((left, right) => (left.slotNumber ?? 0) - (right.slotNumber ?? 0));

      const activeSlots = daySchedule.filter(item => item.available);
      if (activeSlots.length === 0) {
        return;
      }

      activeSlots.forEach(slot => {
        if (!slot.start || !slot.end || slot.start >= slot.end) {
          errors.push(strings.editSchedule.invalidAvailabilityPeriod(slot.day));
        }
      });

      if (activeSlots.length > 1) {
        const firstSlot = activeSlots.find(item => (item.slotNumber ?? 0) === 0) ?? activeSlots[0];
        const secondSlot = activeSlots.find(item => (item.slotNumber ?? 0) === 1);

        if (!firstSlot || !secondSlot) {
          errors.push(strings.editSchedule.secondAvailabilityRequiresFirst(activeSlots[0].day));
          return;
        }

        if ((firstSlot.end || '') > (secondSlot.start || '')) {
          errors.push(strings.editSchedule.availabilityPeriodsCannotOverlap(firstSlot.day));
        }
      }
    });

    return errors;
  }, [schedule]);

  const onSubmit = () => {
    if (validationErrors.length > 0) {
      return;
    }

    setModalOpen(true);
  };

  const goBack = () => {
    navigate(-1);
  };

  const onSave = async () => {
    const result = await updatePattern({
      args: {
        employeeId: userId,
        patterns: employeeScheduleToPattern(schedule)
      }
    });

    await handleMutationResult(result, {
      successMessage: 'Schedule successfully updated.',
      onSuccess: goBack
    });
  };

  return (
    <>
      <TSCard className="space-y-5 max-w-3xl mx-auto p-5">
        <div
          className="grid grid-cols-3 gap-y-4 gap-x-2 sm:gap-x-6"
          style={{ gridTemplateColumns: 'minmax(120px, auto) minmax(0, 1.65fr) minmax(0, 1.65fr)' }}
        >
          <div></div>
          <p className="text-center">{strings.editSchedule.availableFrom}</p>
          <p className="text-center">{strings.editSchedule.availableUntil}</p>
          {dayIndexes.flatMap(dayNumber => {
            const daySchedule = getDaySchedule(dayNumber);

            return daySchedule.map((item, index) => (
              <TeamMemberScheduleEditFormRow
                key={`${item.dayNumber}-${item.slotNumber}`}
                schedule={item}
                showDayLabel={index === 0}
                onCheck={index === 0 ? onCheck(item) : undefined}
                onStartChange={onStartChange(item)}
                onEndChange={onEndChange(item)}
                canAdd={index === 0 && canAddSplitPeriod(item.dayNumber)}
                canDelete={daySchedule.length > 1}
                onAdd={onAdd(item)}
                onDelete={onDelete(item)}
                addButtonLabel={strings.editSchedule.addAvailabilityPeriod}
                deleteButtonLabel={strings.editSchedule.deleteAvailabilityPeriod}
              />
            ));
          })}
        </div>
        {validationErrors.length > 0 ? (
          <div className="space-y-1">
            {validationErrors.map(error => (
              <p className="text-sm text-red-500 leading-5" key={error}>
                {error}
              </p>
            ))}
          </div>
        ) : null}
        <ConfirmDialog
          title={strings.editSchedule.editTitle}
          confirmLabel={strings.editSchedule.confirm}
          cancelLabel={strings.common.cancel}
          open={modalOpen}
          onConfirm={onSave}
          onClose={() => setModalOpen(false)}
          loading={updatePatternRes.fetching}
        >
          {isSelf ? strings.editSchedule.selfModalText : strings.editSchedule.modalText(userFirstName)}
        </ConfirmDialog>
      </TSCard>
      <div className="space-y-5 max-w-3xl mx-auto pt-5 pl-5">
        <div className="flex w-full items-center justify-end gap-5">
          <Button onClick={goBack}>{strings.common.cancel}</Button>
          <Button primary onClick={onSubmit} disabled={validationErrors.length > 0}>
            {strings.common.save}
          </Button>
        </div>
      </div>
    </>
  );
};
